import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import {
  selectCurrentContextImagesDocuments,
  selectCurrentContextLoading,
  selectCurrentImageDocumentContext,
  selectDocumentCategories,
} from '../../../../../../../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { ImagesDocumentsGalleryPropsWeb } from '@v2/pages/nc-file/details/modules/nc-file/components/displayModes/ImagesDocumentsGallery';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { useTabCount } from '@v2/pages/common/hooks/useTabCount';
import { useCallback, useRef, useState } from 'react';
import { useImageHandlingChecks } from '@v2/components/imageHandling/hooks/useImageHandlingChecks';
import { fetchDocumentCategories } from '@reducers/v2/imageHandlingV2/thunks';
import { isTempID } from '@v2/components/imageHandling/helpers';
import { parseRouteParamAsNumber } from '@helpers';

interface UseManageImagesDocumentsGalleryParams
  extends Pick<
    ImagesDocumentsGalleryPropsWeb,
    'images' | 'setSelectedIndex' | 'selectedIndex' | 'withoutDocTypeButton' | 'withoutImageHandlingHeader'
  > {}

export const useManageImagesDocumentsGallery = ({
  images: imagesFromProps,
  selectedIndex,
  setSelectedIndex,
  withoutDocTypeButton,
  withoutImageHandlingHeader,
}: UseManageImagesDocumentsGalleryParams) => {
  const route = useRoute();
  const navigation = useNavigation<any>();

  const dispatch = useAppDispatch();

  const currentContext = useSelector(selectCurrentImageDocumentContext);
  const imagesFromStore = useSelector(selectCurrentContextImagesDocuments);
  const imagesDocumentsLoading = useSelector(selectCurrentContextLoading);
  const documentCategories = useSelector(selectDocumentCategories);

  const images = imagesFromProps || imagesFromStore;

  const documentId = parseRouteParamAsNumber(route.params?.documentId);
  const selectedImageKey = route?.params?.selectedImageKey;
  const { isSingleImage } = useImageHandlingChecks({ images });

  const { handleImageNavigation } = useHandleImageNavigation();
  const { onSuccessDocTypeChange } = useTabCount();

  const selectedImage = selectedImageKey ? images?.find((image) => image?.contentHash === selectedImageKey) : null;

  const thumbnailScrollRef = useRef(null);

  const [shouldShowWebImageControl, setShouldShowWebImageControl] = useState<boolean>(true);

  const imagesHashes = images?.map((i) => i.contentHash).join(', ');

  useFocusEffect(
    useCallback(() => {
      if (imagesDocumentsLoading) return;

      let newSelectedIndex: number = 0;

      if (images.length === 0) {
        setSelectedIndex(0);
        return;
      }

      newSelectedIndex = selectedImageKey ? images.findIndex((i) => isTempID(i.documentId) || i?.contentHash === selectedImageKey) : 0;

      if (newSelectedIndex === -1) {
        if (documentId) {
          const docIdIndex = images.findIndex((i) => i?.documentId === parseInt(documentId));
          if (docIdIndex >= 0) {
            setSelectedIndex(docIdIndex);
            return;
          }
        }

        newSelectedIndex = 0;
      }

      const validIndex = Math.max(0, Math.min(newSelectedIndex, images.length - 1));

      setSelectedIndex(validIndex);

      navigation.setParams({
        selectedImageKey: images?.[validIndex]?.contentHash,
        ...(documentId ? { documentId: images?.[validIndex]?.documentId } : {}),
      });
    }, [imagesHashes, selectedImageKey, imagesDocumentsLoading, images.length])
  );

  const onChangeSelectedIndex = (index: number) => {
    const validIndex = Math.max(0, Math.min(index, images.length - 1));
    setSelectedIndex(validIndex);
    const newSelectedKey = images[validIndex]?.contentHash;
    const newSelectedDocumentId = images[validIndex]?.documentId;
    navigation.setParams({ selectedImageKey: newSelectedKey, ...(documentId ? { documentId: newSelectedDocumentId } : {}) });
  };

  useFocusEffect(
    useCallback(() => {
      dispatch(fetchDocumentCategories());
    }, [])
  );

  const onImagePress = () => {
    // onChangeSelectedIndex(0);
    handleImageNavigation({ documentId: documentId ?? selectedImage?.documentId, showActions: false });
  };

  const validSelectedIndex = Math.max(0, Math.min(selectedIndex, images.length - 1));

  const webExpandControlConfig = { size: 32, offset: 18, containerSize: 48 };

  const onExpandViewPress = () => {
    // setSelectedIndex(0);
    const documentIdParam = documentId ?? selectedImage?.documentId;
    handleImageNavigation({ documentId: documentIdParam });
  };

  const shouldDisplayDocTypeButton =
    withoutImageHandlingHeader &&
    shouldShowWebImageControl &&
    selectedImage &&
    !imagesDocumentsLoading &&
    !withoutDocTypeButton &&
    documentCategories.loaded;
  const expandViewPosition = (webExpandControlConfig.containerSize - webExpandControlConfig.size) / 2;
  const shouldDisplayExpandImageButton = withoutImageHandlingHeader && shouldShowWebImageControl && selectedImage && !selectedImage?.isDocument;

  return {
    imagesList: images,
    validSelectedIndex,
    onChangeSelectedIndex,
    thumbnailScrollRef,
    isSingleImage,
    onImagePress,
    selectedImage,
    onSuccessDocTypeChange,
    shouldDisplayExpandImageButton,
    shouldDisplayDocTypeButton,
    shouldShowWebImageControl,
    onExpandViewPress,
    expandViewPosition,
    webExpandControlConfig,
    imagesDocumentsLoading,
    imageContext: currentContext,
  };
};
